import React, { useEffect, useMemo, useState } from "react";
import styles from "../styles.modules.scss";
import { FormattedMessage } from "react-intl";
import { Button } from "../../../button";
import { useElementOnScreen } from "../../../../hooks/use-element-on-screen";
import { useSendCommand } from "../../../../hooks/use-send-command";
import { useOpenNewsArticle } from "../../../../hooks/use-open-news-article";
import { handleCTAClick } from "..";
import { parseCTAs } from "../parse-ctas";
import { usePollContext } from "../../../../stores/poll";
import { Locale as ContentfulLocale } from "inbox-contentful";
import {
    findRelatedMediaByTitle,
    findRelatedMediaLabelByTitle,
} from "../../../../utils/related-media";
import { Image } from "../../../image";
import classNames from "classnames";
import { bridge } from "../../../../utils/bridge";

export type ProgressSlideType = {
    type: "progressTrackerEntry";
    id: string;
    title: string;
    tracker: ProgressTracker;
    milestones: ProgressMilestone[];
    ctas: InboxCTA[];
    targeting?: Targeting;
    lang: Locale;
    relatedMedia?: RelatedMedia[];
};

export const SpireCryptProgressSlide = ({
    slide,
    previousDaySlide,
}: {
    slide: ProgressSlideType;
    previousDaySlide?: ProgressSlideType;
}) => {
    const { title, tracker, milestones, targeting, relatedMedia } = slide;

    const [ref, isVisible] = useElementOnScreen<HTMLDivElement>({
        threshold: 0.001,
    });
    const bottomElementRef = React.useRef<HTMLDivElement>(null);
    const firstLockedRef = React.useRef<HTMLDivElement>(null);
    const trackerProgressRef = React.useRef<HTMLDivElement>(null);
    const sendCommand = useSendCommand();
    const openNewsArticle = useOpenNewsArticle();
    const {
        actions: { openModal },
    } = usePollContext();

    const dayNumber = Number(
        relatedMedia?.find((media) => media.title === "day-number")?.label ?? 0,
    );

    const hasSeenBefore =
        localStorage.getItem("spire-crypt-seen-day-last") === "true";

    const [startSequencePhase, setStartSequencePhase] = useState<
        "start" | "playing" | "end"
    >(hasSeenBefore ? "end" : "start");

    const [previousRewardsIsOpen, setPreviousRewardsIsOpen] = useState(false);
    const togglePreviousRewardsIsOpen = () => {
        setPreviousRewardsIsOpen((prev) => !prev);
    };

    useEffect(() => {
        if (isVisible && startSequencePhase === "start") {
            setTimeout(() => {
                setStartSequencePhase("playing");
            }, 1000);

            setTimeout(() => {
                setStartSequencePhase("end");
                localStorage.setItem("spire-crypt-seen-day-last", "true");
            }, 4000);
        }
    }, [startSequencePhase, isVisible]);

    const handleClick = handleCTAClick(
        sendCommand,
        openNewsArticle,
        openModal,
        title,
    );

    const relatedMediaStyles = useMemo(() => {
        const styles: React.CSSProperties & { [key: string]: string } = {};

        const imageMap = {
            horizon: "--horizon-image",
            icon: "--icon-image",
            "background-layer": "--background-layer-image",
            "background-layer-2": "--background-layer-2-image",
            "reward-1": "--reward-1-image",
            "reward-2": "--reward-2-image",
            "reward-3": "--reward-3-image",
            "reward-4": "--reward-4-image",
            "reward-5": "--reward-5-image",
            "reward-6": "--reward-6-image",
            "reward-7": "--reward-7-image",
            "reward-8": "--reward-8-image",
            "reward-9": "--reward-9-image",
            "reward-10": "--reward-10-image",
            "reward-11": "--reward-11-image",
            "reward-12": "--reward-12-image",
            "reward-13": "--reward-13-image",
            "reward-14": "--reward-14-image",
            "reward-15": "--reward-15-image",
            "reward-16": "--reward-16-image",
            "reward-17": "--reward-17-image",
            "reward-18": "--reward-18-image",
            "reward-19": "--reward-19-image",
            "reward-20": "--reward-20-image",
        };

        const labelMap = {
            horizon: "--horizon-color",
        };

        Object.entries(imageMap).forEach(([title, cssVar]) => {
            const imageUrl = findRelatedMediaByTitle(relatedMedia ?? [], title);
            if (imageUrl) styles[cssVar] = `url("${imageUrl}")`;
        });

        Object.entries(labelMap).forEach(([title, cssVar]) => {
            const label = findRelatedMediaLabelByTitle(
                relatedMedia ?? [],
                title,
            );
            if (label) styles[cssVar] = label;
        });

        return styles;
    }, [relatedMedia]);

    const previousDayRelatedMediaStyles = useMemo(() => {
        const styles: React.CSSProperties & { [key: string]: string } = {};

        const imageMap = {
            horizon: "--previous-day-horizon-image",
            "background-layer": "--previous-day-background-layer-image",
            "background-layer-2": "--previous-day-background-layer-2-image",
        };

        const labelMap = {
            horizon: "--previous-day-horizon-color",
        };

        Object.entries(imageMap).forEach(([title, cssVar]) => {
            const imageUrl = findRelatedMediaByTitle(
                previousDaySlide?.relatedMedia ?? [],
                title,
            );
            if (imageUrl) styles[cssVar] = `url("${imageUrl}")`;
        });

        Object.entries(labelMap).forEach(([title, cssVar]) => {
            const label = findRelatedMediaLabelByTitle(
                previousDaySlide?.relatedMedia ?? [],
                title,
            );
            if (label) styles[cssVar] = label;
        });

        return styles;
    }, [previousDaySlide?.relatedMedia]);

    React.useEffect(() => {
        if (bottomElementRef.current && isVisible) {
            const top = bottomElementRef.current.offsetTop;
            ref.current?.parentElement?.scrollTo({
                top,
            });
        }
    }, [bottomElementRef.current, isVisible]);

    //

    React.useEffect(() => {
        setTimeout(() => {
            if (firstLockedRef.current && isVisible) {
                // Scroll the top of the filled bar to the center of the view
                const trackerProgressOffsetTop =
                    trackerProgressRef.current?.offsetTop ?? 0;
                const trackersOffsetTop =
                    firstLockedRef.current.parentElement?.parentElement
                        ?.offsetTop ?? 0;
                const trackersMarginTop = firstLockedRef.current.parentElement
                    ?.parentElement
                    ? parseFloat(
                          getComputedStyle(
                              firstLockedRef.current.parentElement
                                  ?.parentElement,
                          ).marginTop,
                      )
                    : 0;
                const contentPaddingTop = firstLockedRef.current.parentElement
                    ?.parentElement?.parentElement
                    ? parseFloat(
                          getComputedStyle(
                              firstLockedRef.current.parentElement
                                  ?.parentElement?.parentElement,
                          ).paddingTop,
                      )
                    : 0;

                const top =
                    trackerProgressOffsetTop +
                    trackersOffsetTop +
                    trackersMarginTop +
                    contentPaddingTop -
                    (ref.current?.parentElement?.clientHeight ?? 0) / 1.5;

                ref.current?.parentElement?.scrollTo({
                    top,
                    behavior: "smooth",
                });
            }
        }, 500);
    }, [bottomElementRef.current, isVisible]);

    const [remainingTime, setRemainingTime] = useState<{
        remainingSeconds: number | null;
        remainingHours: number;
        remainderMinutes: number;
    }>({
        remainingSeconds: null,
        remainingHours: 0,
        remainderMinutes: 0,
    });

    const timerString =
        startSequencePhase === "start" || startSequencePhase === "playing"
            ? previousDaySlide?.relatedMedia?.find(
                  (media) => media.title === "timer",
              )?.label
            : relatedMedia?.find((media) => media.title === "timer")?.label;

    const endTime = timerString ? new Date(timerString).getTime() : 0;

    const rewards =
        relatedMedia
            ?.find((media) => media.title === "day-rewards")
            ?.label?.split(",") ?? [];

    useEffect(() => {
        const timer = async () => {
            const timeData = {
                secondsLeft: Math.floor((endTime - Date.now()) / 1000),
                secondsLeftTimestampMs:
                    Date.now() +
                    Math.floor((endTime - Date.now()) / 1000) * 1000,
            };

            const remainingSeconds = timeData.secondsLeft;

            let remainingHours = Math.floor(remainingSeconds / 3600);

            const remainderSeconds = remainingSeconds - remainingHours * 3600;

            let remainderMinutes = Math.ceil(remainderSeconds / 60);

            if (remainderMinutes === 60) {
                remainingHours += 1;
                remainderMinutes = 0;
            }

            setRemainingTime({
                remainingSeconds,
                remainingHours,
                remainderMinutes,
            });
        };
        timer();
        const interval = setInterval(() => {
            timer();
        }, 1000);
        return () => clearInterval(interval);
    }, [endTime]);

    const ctas = parseCTAs(slide.ctas, handleClick);

    const fillPercentage = tracker.progress;
    const barColor = tracker.color;

    const style = {
        width: `${Math.max(fillPercentage, 1.25)}%`,
    };

    const previousDayStyle = {
        width: `${Math.max(previousDaySlide?.tracker?.progress ?? 0, 1.25)}%`,
    };

    const midWayStyle = {
        width: "1.25%",
    };

    const hasMileStoneLabels = milestones.some(
        (milestone) => !!milestone.label,
    );

    const barStyle = {
        "--bar-color": barColor,
        "--previous-day-bar-color": previousDaySlide?.tracker.color ?? barColor,
        height: "100%",
        ...relatedMediaStyles,
        ...previousDayRelatedMediaStyles,
    } as React.CSSProperties;

    const translate = (
        id:
            | "hoursAbbreviated"
            | "minutesAbbreviated"
            | "previousRewards"
            | "dayReward"
            | "noReward"
            | `reward${number}Name`,
    ) => {
        type Translations = {
            hoursAbbreviated: string;
            minutesAbbreviated: string;
            previousRewards: string;
            dayReward: string;
            noReward: string;
            reward1Name: string;
            reward2Name: string;
            reward3Name: string;
            reward4Name: string;
            reward5Name: string;
            reward6Name: string;
            reward7Name: string;
            reward8Name: string;
            reward9Name: string;
            reward10Name: string;
            reward11Name: string;
            reward12Name: string;
            reward13Name: string;
            reward14Name: string;
            reward15Name: string;
            reward16Name: string;
            reward17Name: string;
            reward18Name: string;
            reward19Name: string;
            reward20Name: string;
        };

        const translations: {
            [key in ContentfulLocale]: Translations;
        } = {
            de: {
                hoursAbbreviated: "H",
                minutesAbbreviated: "M",
                previousRewards: "Vorherige Belohnungen",
                dayReward: `Belohnung – Tag ${dayNumber}`,
                noReward: "Keine Belohnung",
                reward1Name: "1 Juwel",
                reward2Name: "Schwert-Spray",
                reward3Name: "Fackel-Spray",
                reward4Name: "200 Münzen",
                reward5Name: "Karneval-Spielericon",
                reward6Name: "Kritischer-Fehler-Icon",
                reward7Name: "Roboterritter-Icon",
                reward8Name: "Blingbox-Emote",
                reward9Name: "1000 Bling",
                reward10Name: "200 Credits",
                reward11Name: "300 Powerpunkte",
                reward12Name: "500 Münzen",
                reward13Name: "10 zufällige Starr-Drops",
                reward14Name: "Stargold-Doug-Skin",
                reward15Name: "Legendärer Starr-Drop",
                reward16Name: "500 Credits",
                reward17Name: "1000 Münzen",
                reward18Name: "Ultra-Trophäenbox",
                reward19Name: "Hyperladung-Drop",
                reward20Name: "Ritter-Doug-Skin",
            },
            en: {
                hoursAbbreviated: "h",
                minutesAbbreviated: "m",
                previousRewards: "Previous Rewards",
                dayReward: `Day ${dayNumber} Reward`,
                noReward: "No Reward",
                reward1Name: "1 gem",
                reward2Name: "Sword Spray",
                reward3Name: "Torch Spray",
                reward4Name: "200 Coins",
                reward5Name: "Carnaval Player Icon",
                reward6Name: "Critical Failure Icon",
                reward7Name: "Robot Knight Icon",
                reward8Name: "Bling Box Emote",
                reward9Name: "1000 Bling",
                reward10Name: "200 Credits",
                reward11Name: "300 Power Points",
                reward12Name: "500 Coins",
                reward13Name: "10 Random Starr Drops",
                reward14Name: "True Gold Doug Skin",
                reward15Name: "Legendary Starr Drop",
                reward16Name: "500 Credits",
                reward17Name: "1000 Coins",
                reward18Name: "Ultra Trophy Box",
                reward19Name: "Hypercharge Drop",
                reward20Name: "Knight Doug Skin",
            },
            es: {
                hoursAbbreviated: "h",
                minutesAbbreviated: "min",
                previousRewards: "Recompensas anteriores",
                dayReward: `Recompensa del día ${dayNumber}`,
                noReward: "Ninguna recompensa",
                reward1Name: "1 gema",
                reward2Name: "Spray de espada",
                reward3Name: "Spray de antorcha",
                reward4Name: "200 monedas",
                reward5Name: "Icono de jugador de carnaval",
                reward6Name: "Icono de fallo crítico",
                reward7Name: "Icono de caballero robótico",
                reward8Name: "Reacción de caja de blines",
                reward9Name: "1000 blines",
                reward10Name: "200 créditos",
                reward11Name: "300 puntos de fuerza",
                reward12Name: "500 monedas",
                reward13Name: "10 premios Starr aleatorios",
                reward14Name: "Aspecto de Doug dorado",
                reward15Name: "Premio Starr legendario",
                reward16Name: "500 créditos",
                reward17Name: "1000 monedas",
                reward18Name: "Caja de trofeos ultra",
                reward19Name: "Una hipercarga",
                reward20Name: "Aspecto de Doug caballero",
            },
            fr: {
                hoursAbbreviated: "h",
                minutesAbbreviated: "min",
                previousRewards: "Récompenses précédentes",
                dayReward: `Récompense du jour ${dayNumber}`,
                noReward: "Aucune récompense",
                reward1Name: "1 gemme",
                reward2Name: "Tag épée",
                reward3Name: "Tag torche",
                reward4Name: "200 pièces",
                reward5Name: "Icône de joueur carnaval",
                reward6Name: "Icône échec critique",
                reward7Name: "Icône chevalier robot",
                reward8Name: "Émote boîte de blings",
                reward9Name: "1 000 blings",
                reward10Name: "200 crédits",
                reward11Name: "300 points de pouvoir",
                reward12Name: "500 pièces",
                reward13Name: "10 prix Starr aléatoires",
                reward14Name: "Skin Doug or massif",
                reward15Name: "Prix Starr légendaire",
                reward16Name: "500 crédits",
                reward17Name: "1 000 pièces",
                reward18Name: "Ultra boîte des trophées",
                reward19Name: "Prix d'hypercharge",
                reward20Name: "Skin Doug chevalier",
            },
            it: {
                hoursAbbreviated: "h",
                minutesAbbreviated: "m",
                previousRewards: "Premi precedenti",
                dayReward: `Premio del ${dayNumber}º giorno`,
                noReward: "Nessuna ricompensa",
                reward1Name: "Una gemma",
                reward2Name: "Graffito della spada",
                reward3Name: "Graffito della torcia",
                reward4Name: "200 monete",
                reward5Name: "Icona del giocatore del carnevale",
                reward6Name: "Icona del fallimento critico",
                reward7Name: "Icona del cavaliere robot",
                reward8Name: "Reazione della cassa di bling",
                reward9Name: "1000 bling",
                reward10Name: "200 crediti",
                reward11Name: "300 punti energia",
                reward12Name: "500 monete",
                reward13Name: "10 premi Starr casuali",
                reward14Name: "Skin di Doug 24 carati",
                reward15Name: "Premio Starr leggendario",
                reward16Name: "500 crediti",
                reward17Name: "1000 monete",
                reward18Name: "Cassa ultra dei trofei",
                reward19Name: "Premio Starr con overdrive",
                reward20Name: "Skin di Doug cavaliere",
            },
            ja: {
                hoursAbbreviated: "時間",
                minutesAbbreviated: "分",
                previousRewards: "前の報酬",
                dayReward: `${dayNumber}日目の報酬`,
                noReward: "報酬なし",
                reward1Name: "1エメラルド",
                reward2Name: "スプレー「剣」",
                reward3Name: "スプレー「たいまつ」",
                reward4Name: "200コイン",
                reward5Name: "プレイヤーアイコン「カーニバル」",
                reward6Name: "アイコン「大失敗」",
                reward7Name: "アイコン「ロボット騎士」",
                reward8Name: "スタンプ「ジュエルチップボックス」",
                reward9Name: "1000ジュエルチップ",
                reward10Name: "200クレジット",
                reward11Name: "300パワーポイント",
                reward12Name: "500コイン",
                reward13Name: "ランダムスタードロップ10個",
                reward14Name: "スキン「トゥルーゴールド ダグ」",
                reward15Name: "レジェンドレアスタードロップ",
                reward16Name: "500クレジット",
                reward17Name: "1000コイン",
                reward18Name: "ウルトラトロフィーボックス",
                reward19Name: "ハイパーチャージドロップ",
                reward20Name: "スキン「騎士 ダグ」",
            },
            ko: {
                hoursAbbreviated: "시간",
                minutesAbbreviated: "분",
                previousRewards: "이전 보상",
                dayReward: `${dayNumber}일차 보상`,
                noReward: "보상 없음",
                reward1Name: "보석 1개",
                reward2Name: "검 스프레이",
                reward3Name: "횃불 스프레이",
                reward4Name: "코인 200개",
                reward5Name: "카니발 플레이어 아이콘",
                reward6Name: "치명적인 실패 아이콘",
                reward7Name: "로봇 기사 아이콘",
                reward8Name: "블링 상자 이모티콘",
                reward9Name: "블링 1,000개",
                reward10Name: "200 크레딧",
                reward11Name: "300 PP",
                reward12Name: "코인 500개",
                reward13Name: "무작위 스타 드롭 10개",
                reward14Name: "트루 골드 더그 스킨",
                reward15Name: "전설 스타 드롭",
                reward16Name: "500 크레딧",
                reward17Name: "코인 1,000개",
                reward18Name: "울트라 트로피 상자",
                reward19Name: "하이퍼차지 드롭",
                reward20Name: "기사 더그 스킨",
            },
            pl: {
                hoursAbbreviated: "h",
                minutesAbbreviated: "m",
                previousRewards: "Poprzednie nagrody",
                dayReward: `Nagroda za dzień ${dayNumber}`,
                noReward: "Brak nagrody",
                reward1Name: "1 klejnot",
                reward2Name: "Sprej miecza",
                reward3Name: "Sprej pochodni",
                reward4Name: "200 monet",
                reward5Name: "Karnawałowa ikona gracza",
                reward6Name: "Ikona krytycznej porażki",
                reward7Name: "Ikona robotycznego rycerza",
                reward8Name: "Emotka skrzynki błyskotek",
                reward9Name: "1000 błyskotek",
                reward10Name: "200 kredytów",
                reward11Name: "300 punktów mocy",
                reward12Name: "500 monet",
                reward13Name: "10 losowych dropów Starr",
                reward14Name: "Przebranie Złoty Doug",
                reward15Name: "Legendarny drop Starr",
                reward16Name: "500 kredytów",
                reward17Name: "1000 monet",
                reward18Name: "Ultraskrzynka pucharów",
                reward19Name: "Drop hiperdoładowania",
                reward20Name: "Przebranie Rycerz Doug",
            },
            pt: {
                hoursAbbreviated: "h",
                minutesAbbreviated: "m",
                previousRewards: "Recompensas anteriores",
                dayReward: `Recompensa do dia ${dayNumber}`,
                noReward: "Nenhuma recompensa",
                reward1Name: "1 gema",
                reward2Name: "Spray de espada",
                reward3Name: "Spray de tocha",
                reward4Name: "200 moedas",
                reward5Name: "Ícone de jogador de Carnaval",
                reward6Name: "Ícone de falha crítica",
                reward7Name: "Ícone de cavaleiro robô",
                reward8Name: "Reação de caixa de blings",
                reward9Name: "1.000 blings",
                reward10Name: "200 vales",
                reward11Name: "300 pontos de poder",
                reward12Name: "500 moedas",
                reward13Name: "10 sorteios Starr aleatórios",
                reward14Name: "Visual Doug Dourado",
                reward15Name: "Sorteio Starr lendário",
                reward16Name: "500 vales",
                reward17Name: "1.000 moedas",
                reward18Name: "Ultracaixa de troféus",
                reward19Name: "Sorteio de hipercarga",
                reward20Name: "Visual Doug Cavaleiro",
            },
            ru: {
                hoursAbbreviated: " ч.",
                minutesAbbreviated: " м.",
                previousRewards: "Предыдущие награды",
                dayReward: `Награда ${dayNumber}-го дня`,
                noReward: "Нет награды",
                reward1Name: "1 кристалл",
                reward2Name: "Спрей «Меч»",
                reward3Name: "Спрей «Факел»",
                reward4Name: "200 монет",
                reward5Name: "Иконка игрока «Карнавал»",
                reward6Name: "Иконка «Критический провал»",
                reward7Name: "Иконка «Рыцарь-робот»",
                reward8Name: "Эмодзи «Ящик с блингами»",
                reward9Name: "1000 блингов",
                reward10Name: "200 кредитов",
                reward11Name: "300 очков силы",
                reward12Name: "500 монет",
                reward13Name: "10 случайных призов Старр",
                reward14Name: "Скин «Золотой Даг»",
                reward15Name: "Легендарный приз Старр",
                reward16Name: "500 кредитов",
                reward17Name: "1000 монет",
                reward18Name: "Трофейный ультраящик",
                reward19Name: "Гиперзарядный приз",
                reward20Name: "Скин «Рыцарь Даг»",
            },
            tr: {
                hoursAbbreviated: "sa",
                minutesAbbreviated: "dk",
                previousRewards: "Önceki Ödüller",
                dayReward: `${dayNumber}. Gün Ödülü`,
                noReward: "Ödül yok",
                reward1Name: "1 Elmas",
                reward2Name: "Kılıç Spreyi",
                reward3Name: "Meşale Spreyi",
                reward4Name: "200 Altın",
                reward5Name: "Karnaval Oyuncu Simgesi",
                reward6Name: "Kritik Hata Simgesi",
                reward7Name: "Robot Şövalye Simgesi",
                reward8Name: "Mücevher Kutusu İfadesi",
                reward9Name: "1000 Mücevher",
                reward10Name: "200 Kredi",
                reward11Name: "300 Güç Puanı",
                reward12Name: "500 Altın",
                reward13Name: "10 Rastgele Starr Hediyesi",
                reward14Name: "Saf Altın Doug Kostümü",
                reward15Name: "Efsanevi Starr Hediyesi",
                reward16Name: "500 Kredi",
                reward17Name: "1000 Altın",
                reward18Name: "Ultra Kupa Kutusu",
                reward19Name: "Hiperşarj Hediyesi",
                reward20Name: "Şövalye Doug Kostümü",
            },
            "zh-hans": {
                hoursAbbreviated: "小时",
                minutesAbbreviated: "分",
                previousRewards: "先前奖励",
                dayReward: `第${dayNumber}天奖励`,
                noReward: "无奖励",
                reward1Name: "1宝石",
                reward2Name: "剑形喷漆",
                reward3Name: "火炬喷漆",
                reward4Name: "200金币",
                reward5Name: "狂欢节头像",
                reward6Name: "大失败头像",
                reward7Name: "机器人骑士头像",
                reward8Name: "闪闪宝箱表情",
                reward9Name: "1000闪闪币",
                reward10Name: "200英雄券",
                reward11Name: "300战力能量",
                reward12Name: "500金币",
                reward13Name: "10个随机星妙惊喜",
                reward14Name: "辉金道格皮肤",
                reward15Name: "传奇星妙惊喜",
                reward16Name: "500英雄券",
                reward17Name: "1000金币",
                reward18Name: "究极荣誉宝箱",
                reward19Name: "极限充能惊喜",
                reward20Name: "邪灵骑士道格皮肤",
            },
            zh: {
                hoursAbbreviated: "小時",
                minutesAbbreviated: "分",
                previousRewards: "先前獎勵",
                dayReward: `第${dayNumber}天獎勵`,
                noReward: "無獎勵",
                reward1Name: "1寶石",
                reward2Name: "劍形噴漆",
                reward3Name: "火炬噴漆",
                reward4Name: "200金幣",
                reward5Name: "狂歡節頭像",
                reward6Name: "大失敗頭像",
                reward7Name: "機器人騎士頭像",
                reward8Name: "閃閃寶箱表情",
                reward9Name: "1000閃光幣",
                reward10Name: "200英雄積分",
                reward11Name: "300能量點數",
                reward12Name: "500金幣",
                reward13Name: "10個隨機寶物之星",
                reward14Name: "璀璨金道格造型",
                reward15Name: "傳奇寶物之星",
                reward16Name: "500英雄積分",
                reward17Name: "1000金幣",
                reward18Name: "究極榮耀寶箱",
                reward19Name: "極限威能驚喜",
                reward20Name: "邪靈騎士造型",
            },
            // Likely unused
            da: {
                hoursAbbreviated: "t",
                minutesAbbreviated: "min",
                previousRewards: "Tidligere belønninger",
                dayReward: "",
                noReward: "",
                reward1Name: "",
                reward2Name: "",
                reward3Name: "",
                reward4Name: "",
                reward5Name: "",
                reward6Name: "",
                reward7Name: "",
                reward8Name: "",
                reward9Name: "",
                reward10Name: "",
                reward11Name: "",
                reward12Name: "",
                reward13Name: "",
                reward14Name: "",
                reward15Name: "",
                reward16Name: "",
                reward17Name: "",
                reward18Name: "",
                reward19Name: "",
                reward20Name: "",
            },
            ar: {
                hoursAbbreviated: " س",
                minutesAbbreviated: " د",
                previousRewards: "",
                dayReward: "",
                noReward: "",
                reward1Name: "",
                reward2Name: "",
                reward3Name: "",
                reward4Name: "",
                reward5Name: "",
                reward6Name: "",
                reward7Name: "",
                reward8Name: "",
                reward9Name: "",
                reward10Name: "",
                reward11Name: "",
                reward12Name: "",
                reward13Name: "",
                reward14Name: "",
                reward15Name: "",
                reward16Name: "",
                reward17Name: "",
                reward18Name: "",
                reward19Name: "",
                reward20Name: "",
            },
            fi: {
                hoursAbbreviated: "t",
                minutesAbbreviated: "min",
                previousRewards: "Aiemmat palkinnot",
                dayReward: "",
                noReward: "",
                reward1Name: "",
                reward2Name: "",
                reward3Name: "",
                reward4Name: "",
                reward5Name: "",
                reward6Name: "",
                reward7Name: "",
                reward8Name: "",
                reward9Name: "",
                reward10Name: "",
                reward11Name: "",
                reward12Name: "",
                reward13Name: "",
                reward14Name: "",
                reward15Name: "",
                reward16Name: "",
                reward17Name: "",
                reward18Name: "",
                reward19Name: "",
                reward20Name: "",
            },
            id: {
                hoursAbbreviated: "j",
                minutesAbbreviated: "m",
                previousRewards: "Hadiah Sebelumnya",
                dayReward: "",
                noReward: "",
                reward1Name: "",
                reward2Name: "",
                reward3Name: "",
                reward4Name: "",
                reward5Name: "",
                reward6Name: "",
                reward7Name: "",
                reward8Name: "",
                reward9Name: "",
                reward10Name: "",
                reward11Name: "",
                reward12Name: "",
                reward13Name: "",
                reward14Name: "",
                reward15Name: "",
                reward16Name: "",
                reward17Name: "",
                reward18Name: "",
                reward19Name: "",
                reward20Name: "",
            },
            ms: {
                hoursAbbreviated: "j",
                minutesAbbreviated: "m",
                previousRewards: "Ganjaran Sebelumnya",
                dayReward: "",
                noReward: "",
                reward1Name: "",
                reward2Name: "",
                reward3Name: "",
                reward4Name: "",
                reward5Name: "",
                reward6Name: "",
                reward7Name: "",
                reward8Name: "",
                reward9Name: "",
                reward10Name: "",
                reward11Name: "",
                reward12Name: "",
                reward13Name: "",
                reward14Name: "",
                reward15Name: "",
                reward16Name: "",
                reward17Name: "",
                reward18Name: "",
                reward19Name: "",
                reward20Name: "",
            },
            nl: {
                hoursAbbreviated: "u",
                minutesAbbreviated: "m",
                previousRewards: "Vorige beloningen",
                dayReward: "",
                noReward: "",
                reward1Name: "",
                reward2Name: "",
                reward3Name: "",
                reward4Name: "",
                reward5Name: "",
                reward6Name: "",
                reward7Name: "",
                reward8Name: "",
                reward9Name: "",
                reward10Name: "",
                reward11Name: "",
                reward12Name: "",
                reward13Name: "",
                reward14Name: "",
                reward15Name: "",
                reward16Name: "",
                reward17Name: "",
                reward18Name: "",
                reward19Name: "",
                reward20Name: "",
            },
            th: {
                hoursAbbreviated: "ชม.",
                minutesAbbreviated: "น.",
                previousRewards: "รางวัลก่อนหน้า",
                dayReward: "",
                noReward: "",
                reward1Name: "",
                reward2Name: "",
                reward3Name: "",
                reward4Name: "",
                reward5Name: "",
                reward6Name: "",
                reward7Name: "",
                reward8Name: "",
                reward9Name: "",
                reward10Name: "",
                reward11Name: "",
                reward12Name: "",
                reward13Name: "",
                reward14Name: "",
                reward15Name: "",
                reward16Name: "",
                reward17Name: "",
                reward18Name: "",
                reward19Name: "",
                reward20Name: "",
            },
            vi: {
                hoursAbbreviated: "giờ",
                minutesAbbreviated: "ph",
                previousRewards: "Phần thưởng trước",
                dayReward: "",
                noReward: "",
                reward1Name: "",
                reward2Name: "",
                reward3Name: "",
                reward4Name: "",
                reward5Name: "",
                reward6Name: "",
                reward7Name: "",
                reward8Name: "",
                reward9Name: "",
                reward10Name: "",
                reward11Name: "",
                reward12Name: "",
                reward13Name: "",
                reward14Name: "",
                reward15Name: "",
                reward16Name: "",
                reward17Name: "",
                reward18Name: "",
                reward19Name: "",
                reward20Name: "",
            },
        };

        // @ts-expect-error Tired of this type error
        return translations[slide.lang][id];
    };

    const previousDayReached =
        previousDaySlide !== undefined &&
        previousDaySlide.tracker.progress === 100 &&
        rewards[dayNumber - 2] !== undefined;

    const showPreviousDayPrize =
        previousDaySlide !== undefined &&
        rewards.length > 0 &&
        ((previousDayReached && rewards[dayNumber - 2] !== "locked") ||
            !previousDayReached);

    return (
        <>
            <div
                className={`slide progress-indicator ${styles.slide} ${
                    isVisible ? "visible" : ""
                } ${hasMileStoneLabels ? "with-milestone-labels" : ""} ${
                    targeting?.campaignId ?? ""
                } without-title ${startSequencePhase} ${slide.lang}`}
                ref={ref}
                style={barStyle}
            >
                {showPreviousDayPrize && (
                    <div className="previous-day-wrapper">
                        <div className="previous-day-text">
                            <div className={`title ${styles.title} `}>
                                {translate("dayReward")}
                            </div>
                        </div>
                        <div className="previous-day-reward-wrapper">
                            <div className={`previous-day-reward`}></div>
                            <div className="previous-day-reward-label">
                                {translate(`reward15Name`)}
                                <br />
                                {translate(`reward9Name`)}
                            </div>
                        </div>
                    </div>
                )}
                <div className={`horizon ${styles.horizon}`}></div>
                <div
                    className={`background-layer ${styles["background-layer"]}`}
                ></div>
                <div
                    className={`background-layer-2 ${styles["background-layer-2"]}`}
                ></div>
                <div
                    className={`maintenanceTimeContainer ${styles.maintenanceTimeContainer}`}
                >
                    <MaintenanceClock />
                    <div
                        className={`maintenanceTimeBox ${styles.maintenanceTimeBox}`}
                    >
                        <div
                            className={`maintenanceEstimatedNumberText ${styles.maintenanceEstimatedNumberText}`}
                        >
                            {remainingTime.remainingSeconds !== null ? (
                                remainingTime.remainingSeconds > 0 ? (
                                    <>
                                        {remainingTime.remainingHours}
                                        {translate("hoursAbbreviated")}{" "}
                                        {remainingTime.remainderMinutes}
                                        {translate("minutesAbbreviated")}
                                    </>
                                ) : (
                                    `0${translate("hoursAbbreviated")} 0${translate("minutesAbbreviated")}`
                                )
                            ) : (
                                "..."
                            )}
                        </div>
                    </div>
                </div>
                <div className={`content ${styles.content}`}>
                    {title && (
                        <>
                            <div className={`filter ${styles.filter}`} />

                            <div
                                className={`title ${styles.title} `}
                                data-title={title}
                            >
                                {startSequencePhase === "start" ||
                                startSequencePhase === "playing"
                                    ? previousDaySlide?.title
                                    : title}
                            </div>
                        </>
                    )}
                    <div className={`trackers ${styles.trackers}`}>
                        {isVisible && (
                            <>
                                <div className="tracker-dice-wrapper">
                                    <div className={`tracker-wrapper`}>
                                        <div
                                            className={`tracker ${styles.tracker} ${tracker.progress === 100 ? "full" : ""}`}
                                        >
                                            <div
                                                style={
                                                    startSequencePhase ===
                                                    "start"
                                                        ? previousDayStyle
                                                        : startSequencePhase ===
                                                            "playing"
                                                          ? midWayStyle
                                                          : style
                                                }
                                                className={`tracker-progress ${
                                                    styles["tracker-progress"]
                                                } ${
                                                    fillPercentage > 99
                                                        ? "almost-complete"
                                                        : ""
                                                } ${
                                                    startSequencePhase ===
                                                    "start"
                                                        ? "previous-day"
                                                        : ""
                                                }`}
                                                ref={trackerProgressRef}
                                            ></div>
                                        </div>
                                        <div
                                            className={`icon-container ${styles["icon-container"]}`}
                                            style={
                                                startSequencePhase === "start"
                                                    ? previousDayStyle
                                                    : startSequencePhase ===
                                                        "playing"
                                                      ? midWayStyle
                                                      : style
                                            }
                                        >
                                            <div
                                                className={`icon ${styles.icon}`}
                                            ></div>
                                        </div>

                                        {milestones.map((milestone, index) => {
                                            const milestonePosition = Math.max(
                                                milestone.progress,
                                                5, // 5% is the minimum we support without the UI looking totally wack
                                            );

                                            const style = {
                                                left: `${milestonePosition}%`,
                                            };

                                            return (
                                                <span
                                                    key={index}
                                                    style={style}
                                                    className="milestone-marker"
                                                />
                                            );
                                        })}
                                    </div>

                                    <div
                                        className={`dice-container ${styles["dice-container"]}`}
                                    >
                                        {milestones[0]?.image !== undefined && (
                                            <Image
                                                sources={milestones[0].image}
                                            />
                                        )}
                                    </div>
                                </div>
                            </>
                        )}

                        <div
                            className={`milestone-label-wrapper ${styles["milestone-label-wrapper"]}`}
                        >
                            {milestones.map((milestone, index) => {
                                if (!milestone.label) {
                                    return <div key={index} />;
                                }

                                const milestonePosition = Math.max(
                                    milestone.progress,
                                    5, // 5% is the minimum we support without the UI looking totally wack
                                );

                                const hasReachedMilestone = milestone.unlocked;

                                const style = {
                                    left: `${milestonePosition}%`,
                                };

                                return (
                                    <div
                                        key={index}
                                        style={style}
                                        className={`milestone-label ${
                                            styles["milestone-label"]
                                        } ${
                                            hasReachedMilestone
                                                ? styles["milestone-reached"]
                                                : styles["milestone-unreached"]
                                        } ${
                                            hasReachedMilestone
                                                ? "milestone-reached"
                                                : "milestone-unreached"
                                        }`}
                                        title={milestone.label}
                                    >
                                        {milestone.label}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div ref={bottomElementRef}></div>
                </div>
                <div className="previous-day-background"></div>
            </div>
            <div className={`wrapper ${styles.wrapper}`}>
                {ctas.length ? (
                    <div
                        className={`ctas ${
                            ctas.length === 1 ? "single" : "multi"
                        } ${styles.ctas} ${startSequencePhase}`}
                    >
                        {ctas.map(({ label, variant, onClick }, index) => {
                            return (
                                <Button
                                    key={index}
                                    onClick={onClick}
                                    variant={variant}
                                >
                                    <span className="buttonTextWrapper">
                                        {label || (
                                            <FormattedMessage id="globalText.langGo" />
                                        )}
                                    </span>
                                </Button>
                            );
                        })}
                    </div>
                ) : null}
            </div>
            {dayNumber !== 1 && rewards.length > 0 && (
                <div
                    className={classNames("previousRewardsPopover", {
                        isOpen: previousRewardsIsOpen,
                        isVisible: startSequencePhase === "end",
                    })}
                >
                    <span className="previousRewardsPopoverRewardsLabel">
                        {translate("previousRewards")}
                    </span>

                    <div className="rewards">
                        {rewards.map((reward, index) => (
                            <div
                                className={`${reward} earned ${index === 11 ? "latest last" : ""}`} // We set the latest class to the previous day's reward, by removing 2 from the dayNumber
                                key={index}
                                data-reward={reward}
                                onClick={async () => {
                                    (await bridge()).openShop();
                                }}
                            ></div>
                        ))}
                    </div>

                    <div
                        className="previousRewardsPopoverHandle"
                        onClick={togglePreviousRewardsIsOpen}
                    >
                        <div className="arrow"></div>
                    </div>
                </div>
            )}
        </>
    );
};

function MaintenanceClock() {
    const [rotation, setRotation] = useState(90);

    useEffect(() => {
        const interval = setInterval(() => {
            setRotation((prevRotation) => {
                return prevRotation + 90;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className={`maintenanceClock ${styles.maintenanceClock}`}>
            <div
                className={`maintenanceClockPointer ${styles.maintenanceClockPointer}`}
                style={{ transform: `rotate(${rotation}deg)` }}
            ></div>
        </div>
    );
}
